import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import type { Unsubscribe as FirebaseUnsubscribe } from 'firebase/firestore';
import { db } from './clients';

export type Unsubscribe = () => void;

export interface FirestoreDocumentValue {
  id: string;
  exists: boolean;
  data: Record<string, unknown>;
}

export interface FirestoreCollectionSource {
  subscribeCollection(
    collectionName: string,
    orderByField: string,
    onChange: (documents: FirestoreDocumentValue[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe;
  subscribeDocument(
    collectionName: string,
    documentId: string,
    onChange: (document: FirestoreDocumentValue) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe;
}

function normalize(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map(normalize);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, normalize(entry)]),
    );
  }
  return value;
}

function errorValue(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

function documentValue(snapshot: {
  id: string;
  exists: () => boolean;
  data: () => Record<string, unknown> | undefined;
}): FirestoreDocumentValue {
  return {
    id: snapshot.id,
    exists: snapshot.exists(),
    data: (normalize(snapshot.data() ?? {}) as Record<string, unknown>),
  };
}

export const firestoreClient: FirestoreCollectionSource = {
  subscribeCollection(collectionName, orderByField, onChange, onError) {
    const source = query(collection(db, collectionName), orderBy(orderByField, 'asc'));
    const unsubscribe: FirebaseUnsubscribe = onSnapshot(
      source,
      (snapshot) => onChange(snapshot.docs.map(documentValue)),
      (error) => onError?.(errorValue(error)),
    );
    return unsubscribe;
  },
  subscribeDocument(collectionName, documentId, onChange, onError) {
    const reference = doc(db, collectionName, documentId);
    const unsubscribe: FirebaseUnsubscribe = onSnapshot(
      reference,
      (snapshot) => onChange(documentValue(snapshot)),
      (error) => onError?.(errorValue(error)),
    );
    return unsubscribe;
  },
};
